import { CheckCircle2, Pencil, Trash2, ExternalLink } from "lucide-react";
import { fmtAmount, fmtDate, fmtUSD2, TYPE_LABEL } from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const typeColor = {
  income: "bg-income/15 text-income border-income/30",
  expense: "bg-expense/15 text-expense border-expense/30",
  transfer: "bg-transfer/15 text-transfer border-transfer/30",
};

export default function OperationsTable({ operations, onConfirm, onEdit, onDelete }) {
  if (!operations?.length)
    return (
      <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="operations-empty">
        Операций за выбранный период нет
      </div>
    );

  return (
    <div className="overflow-x-auto rounded-lg border" data-testid="operations-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead>Дата</TableHead>
            <TableHead>Тип</TableHead>
            <TableHead>Название</TableHead>
            <TableHead className="hidden md:table-cell">Проект</TableHead>
            <TableHead className="hidden md:table-cell">Категория</TableHead>
            <TableHead className="hidden lg:table-cell">Контрагент</TableHead>
            <TableHead className="text-right">Сумма</TableHead>
            <TableHead className="text-right">USD</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead className="text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {operations.map((o) => (
            <TableRow key={o.id} data-testid={`operation-row-${o.id}`} className="row-anim">
              <TableCell className="whitespace-nowrap font-mono text-xs">{fmtDate(o.date)}</TableCell>
              <TableCell>
                <Badge variant="outline" className={typeColor[o.type]} data-testid={`operation-type-${o.id}`}>
                  {TYPE_LABEL[o.type]}
                </Badge>
              </TableCell>
              <TableCell className="max-w-[240px]">
                <div className="truncate font-medium">{o.name}</div>
                {o.type === "transfer" && o.target_company_name && (
                  <div className="truncate text-xs text-muted-foreground">
                    {o.company_name} → {o.target_company_name}
                  </div>
                )}
                {!!o.links?.length && (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {o.links.map((l, i) => (
                      <a
                        key={`${o.id}-l-${i}`}
                        href={l}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                        data-testid={`operation-link-${o.id}-${i}`}
                      >
                        <ExternalLink className="h-3 w-3" /> ссылка
                      </a>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{o.project_name || "—"}</TableCell>
              <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{o.category_name || "—"}</TableCell>
              <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{o.counterparty_name || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-right font-mono text-sm">{fmtAmount(o.amount, o.currency)}</TableCell>
              <TableCell className="whitespace-nowrap text-right font-mono text-sm font-semibold">
                {fmtUSD2(o.amount_usd)}
                {o.type === "transfer" && !!o.tax && (
                  <div className="text-[11px] font-normal text-muted-foreground" data-testid={`operation-tax-${o.id}`}>
                    налог {o.tax}% → списано {fmtUSD2(o.total_out_usd)}
                  </div>
                )}
              </TableCell>
              <TableCell>
                {o.status === "confirmed" ? (
                  <span className="text-xs text-income" data-testid={`operation-status-${o.id}`}>Подтверждено</span>
                ) : (
                  <span className="text-xs text-warning" data-testid={`operation-status-${o.id}`}>Запланировано</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  {o.status === "planned" && onConfirm && (
                    <Button variant="ghost" size="icon" data-testid={`confirm-operation-${o.id}`} onClick={() => onConfirm(o)}>
                      <CheckCircle2 className="h-4 w-4 text-income" />
                    </Button>
                  )}
                  {onEdit && (
                    <Button variant="ghost" size="icon" data-testid={`edit-operation-${o.id}`} onClick={() => onEdit(o)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button variant="ghost" size="icon" data-testid={`delete-operation-${o.id}`} onClick={() => onDelete(o)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
